<template lang="pug">
	.agency-invoices-list.flex-col.space.tb-padding
		ListView.full-width(ref="listView", initialView="table", :hideViewsSelection="true", :getTotal="(r) => r.TotalNumberOfRecords", :getItems="(r) => prepareData(r.Results)", :getData="getInvoices",
			:columns="columns",
			selectionMode="multiple",
			:searchVisible="true",
			:columnHidingEnabled="false",
			:filterRow="true",
			@selection="onSelection"
		)
			template(v-slot:invoice-num-template="{ data }")
				.flex-row.space.align-center
					a(v-if="!isStationOwner", @click="viewingInvoice = data", :title="$tCaption('Open PDF')")
						i.fas.fa-search
					InvoicesLog(:recordCode="data.RecordCode")
					span {{data.InvoiceNumber}}
			template(v-slot:agency-template="{ data }")
				.flex-row.align-center.space
					template(v-if="$auth.isStationOwner()")
						template(v-if="!data.AgencyId")
							.flex-row(v-if="!specifyAgencyFor || specifyAgencyFor.RecordCode !== data.RecordCode")
								.button.error.flex-row.space.align-center(@click="specifyAgencyFor = data")
									i.fas.fa-edit
									span {{$tCaption('Agency not found')}}
							CompanySelector(v-else, :companyType="2", @select-data="agency => onAgencySelect(data, agency)")
						.unlinked(v-else-if="(data.NotLinkedCompanies || []).filter(f => f.CompanyType === 2).length",
							@click="() => setCompanylinks((data.NotLinkedCompanies || []).filter(f => f.CompanyType === 2)[0])",
							:title="$tCaption('Company is not linked with your account', '@invoices')",
							:data-unlinked-text="$tCaption('Not linked', '@invoices')") {{data.AgencyName}}
						span(v-else) {{data.AgencyName}}
					span(v-else) {{data.AgencyName}}
			template(v-slot:advertiser-template="{ data }")
				.flex-row.align-center.space
					template(v-if="$auth.isStationOwner()")
						template(v-if="data.AdvertiserName && !data.AdvertiserId")
							.flex-row(v-if="!specifyAdvertiserFor || specifyAdvertiserFor.RecordCode !== data.RecordCode")
								.button.error.flex-row.space.align-center(@click="specifyAdvertiserFor = data")
									i.fas.fa-edit
									span {{$tCaption('Advertiser not found')}}
							CompanySelector(v-else, :companyType="0", @select-data="advertiser => onAdvertiserSelect(data, advertiser)")
						.unlinked(v-else-if="(data.NotLinkedCompanies || []).filter(f => f.CompanyType === 0).length",
							@click="() => setCompanylinks((data.NotLinkedCompanies || []).filter(f => f.CompanyType === 0)[0])",
							:title="$tCaption('Company is not linked with your account', '@invoices')",
							:data-unlinked-text="$tCaption('Not linked', '@invoices')") {{data.AdvertiserName}}
						span(v-else) {{data.AdvertiserName}}
					span(v-else) {{data.AdvertiserName}}
			template(slot="left-actions")
				.flex-row.space-large.align-center
					.font-large(v-if="!hideHeader") {{$tCaption('Invoices')}}
					slot(name="header")
					.flex-row.space.align-center
						template
							DxButton(v-if="selectedData.length", text="PDF", icon="fas fa-download", @click="() => download(0)", :disabled="loading")
							DxButton(v-if="selectedData.length", text="EDI", icon="fas fa-download", @click="() => download(1)", :disabled="loading")
						Loader(v-if="loading", :text="$tCaption('Loading')")
		Popup(v-if="viewingInvoice", :popupVisible="!!viewingInvoice", :width="840", :height="1100", @hiding="viewingInvoice = null")
			.agency-invoices-list.flex-row.center
				PdfViewer.pdf-viewer(:src="viewSrc")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Close')", @click="viewingInvoice = null")
		CompanySelector(ref="companySelector", :dropdownVisible="false")
</template>

<script>
	import $ from 'jquery';
	import DxButton from 'devextreme-vue/button';
	import ListView from 'app-core/ListView.vue';
	import Loader from 'app-core/Loader.vue';
	import InvoicesLog from './InvoicesLog.vue';
	import Popup from 'app-core/Popup.vue';
	import Dropdown from 'app-core/Dropdown.vue';
	import PdfViewer from 'app-core/PdfViewer.vue';
	import CompanySelector from 'view/company/CompanySelector.vue';

	export default {
		translationCategory: 'invoice.InvoicesList',
		name: 'agency-invoices-list',
		components: {
			ListView, DxButton, Loader, InvoicesLog, Popup, Dropdown, PdfViewer, CompanySelector
		},
		props: ['isStationOwner', 'hideHeader'],
		data() {
			const agencyStatuses = [() => this.$tCaption('New'), () => this.$tCaption('Viewed'), () => this.$tCaption('Downloaded')];
			const ownerStatuses = [() => this.$tCaption('New'), () => this.$tCaption('Pending validation'), () => this.$tCaption('Validated')];
			return {
				agencyStatuses,
				ownerStatuses,
				columns: [
					{ dataField: 'InvoiceNumber', caption: () => this.$tCaption('Invoice #'), cellTemplate: 'invoice-num-template' },
					...(!this.$auth.isAgency() ? [{ dataField: 'AgencyName', caption: () => this.$tCaption('Agency'), cellTemplate: 'agency-template' }] : []),
					{ dataField: 'ClientCode', caption: () => this.$tCaption('Client code') },
					{ dataField: 'ProductCode', caption: () => this.$tCaption('Product code') },
					{ dataField: 'EstimateCode', caption: () => this.$tCaption('Estimate code') },
					{ dataField: 'BroadcastMonth', caption: () => this.$tCaption('Broadcast month'), dataType: 'date' },
					...(!this.$auth.isAdvertiser() ? [{ dataField: 'AdvertiserName', caption: () => this.$tCaption('Advertiser'), width: 240, cellTemplate: 'advertiser-template' }] : []),
					{ dataField: 'StationName', caption: () => this.$tCaption('Station') },
					{ dataField: 'NetDue', caption: () => this.$tCaption('Net due'), dataType: 'number', format: (v) => this.$formatNumber(v) },
					{ dataField: 'LoadDate', caption: () => this.$tCaption('Created'), dataType: 'date' },
					{ dataField: 'NumberOfSpots', caption: () => this.$tCaption('Number of spots'), dataType: 'number' },
					{
						dataField: 'AgencyStatus',
						caption: !this.$auth.isAgency() ? () => this.$tCaption('Agency status') : () => this.$tCaption('Status'),
						lookup: {
							dataSource: agencyStatuses.map((f, i) => ({ id: i, name: f })),
							displayExpr: 'name',
							valueExpr: 'id'
						}
					},
					...(this.isStationOwner ? [{
						dataField: 'OwnerStatus',
						caption: () => this.$tCaption('Upload status'),
						lookup: {
							dataSource: ownerStatuses.map((f, i) => ({ id: i, name: f })),
							displayExpr: 'name',
							valueExpr: 'id'
						}
					}] : [])
				],
				selectedData: [],
				loading: false,
				viewingInvoice: null,
				specifyAgencyFor: null,
				specifyAdvertiserFor: null
			};
		},
		computed: {
			viewSrc() {
				return this.$Data.getMethodUrl(`externalApi?embedded=true&accessToken=${this.$auth.accessToken()}&method=ElectronicInvoicing/GenerateReport&parameters=${encodeURIComponent(`recordCode=${this.viewingInvoice.RecordCode}&invoiceNumber=${this.viewingInvoice.InvoiceNumber}`)}`);
			}
		},
		mounted() {
			if (__DEBUG__)
				window.invoicesList = this;
		},
		methods: {
			setCompanylinks(item) {
				this.loading = true;
				this.$refs.companySelector.processSelect(item, true).then(r => this.$refs.listView.reload()).finally(() => this.loading = false);
			},
			onAgencySelect(invoice, agency) {
				if (__DEBUG__)
					console.debug('On agency select', { invoice, agency });
				this.specifyAgencyFor = null;
				this.$Data.getExternalApi('ElectronicInvoicing/UpdateInvoiceAgency',`recordCode=${invoice.RecordCode}&agencyId=${agency.Oid}&agencyName=${agency.Name}`).then(r => {
					this.$refs.listView.reload();
				});
			},
			onAdvertiserSelect(invoice, advertiser) {
				if (__DEBUG__)
					console.debug('On advertiser select', { invoice, advertiser });
				this.specifyAdvertiserFor = null;
				this.$Data.getExternalApi('ElectronicInvoicing/UpdateInvoiceAdvertiser',`recordCode=${invoice.RecordCode}&advertiserId=${advertiser.Oid}`).then(r => {
					this.$refs.listView.reload();
				});
			},
			getInvoices(pageIndex, limit, opts) {
				return this.$Data.getInvoices(pageIndex, limit, opts);
			},
			prepareData(data) {
				return (data || []).map(f => ({
					...f,
					AgencyStatusName: this.agencyStatuses[f.AgencyStatus],
					OwnerStatusName: this.ownerStatuses[f.OwnerStatus],
				}));
			},
			onSelection(rows) {
				this.selectedData = rows;
			},
			download(format) {
				var selectedKeys = this.selectedData.map(f => f.RecordCode);
				var selectedNumbers = this.selectedData.map(f => f.InvoiceNumber);
				var fileName = selectedNumbers.length <= 1 ? selectedNumbers[0] : `${selectedNumbers[0]}-${selectedNumbers[selectedNumbers.length-1]}`;
				this.loading = true;
				this.$Data.postExternalApi('ElectronicInvoicing/GenerateReports', {
					Format: format,
					RecordCodes: selectedKeys
				}, {
					xhr: () => {
						var xhr = new XMLHttpRequest();
						xhr.responseType= 'blob';
						return xhr;
					},
					processData: false,
					dataType: 'binary'
				}).then(data => {
					if (__DEBUG__)
						console.debug('GenerateRports response', { data });
					var link = document.createElement('a');
					link.href = window.URL.createObjectURL(data);
					link.download = `Invoices ${fileName}.zip`;
					link.click();
				}).finally(() => this.loading = false);
			},
		}
	};
</script>
<style lang='scss'>
	.agency-invoices-list {
		.pdf-viewer {
			width: 816px;
			height: 978px;
		}

		.button {
			padding: 6px 18px;
			border-radius: 4px;
			text-align: center;
			text-transform: uppercase;
			font-size: 0.8em;
			cursor: pointer;
			color: #fff;

			&.error {
				background: #ff5b5b;
			}

			&.action {
				background: #50a3ff;
			}
		}

		.unlinked {
			color: #ff0000;
			position: relative;

			&:hover {
				cursor: pointer;
				color: #FF9999;
			}

			&:after {
				content: attr(data-unlinked-text);
				position: absolute;
				right: 2px;
				bottom: -16px;
				font-size: 10px;
			}
		}
	}
</style>
